import { Chess, Move } from 'chess.js';

import { applyNullMove } from '@libs/chess/applyNullMove';
import { PromotionPiece, SquareName } from '@libs/chess/types';

export type Action = 'move' | 'premove';
export type AllowedAction = 'move' | 'premove' | 'none';
// TODO: удалить, когда бэк поддержит рокировки королём на ладью
type IPotentialMove = Move & { originalMove?: Move };

/*
    ____,-------------------------------,____
    \   |         State Machine         |   /
    /___|-------------------------------|___\
*/

export type SelectionFSM =
  | SelectionIdle
  | SelectionTargeting
  | SelectionPromoting
  | SelectionSelected;

export function initSelection(s?: {
  from: SquareName;
  to: SquareName;
  action: Action;
}): SelectionFSM {
  return s ? new SelectionSelected(s.action, s.from, s.to) : new SelectionIdle();
}

export class SelectionIdle {
  name = 'idle' as const;

  action: undefined;
  from: undefined;
  to: undefined;
  moves: undefined;
  promotion: undefined;
  toIdle: undefined;
  toSelected: undefined;
  toPromoting: undefined;

  toTargeting(action: Action, from: SquareName, moves: IPotentialMove[], isOutOfOrder = false) {
    return new SelectionTargeting(action, from, moves, isOutOfOrder);
  }

  transition({
    allowedAction,
    from,
    chess,
    allowOutOfOrder,
  }: {
    allowedAction: AllowedAction;
    from: SquareName;
    chess: Chess;
    allowOutOfOrder?: boolean;
  }): SelectionIdle | SelectionTargeting {
    if (allowedAction === 'none') return this;

    const piece = chess.get(from);
    if (!piece) return this;

    const sameColor = piece.color === chess.turn();
    const isOutOfOrder = allowedAction === 'move' && !sameColor && !!allowOutOfOrder;

    if (
      (allowedAction === 'move' && !sameColor && !isOutOfOrder) ||
      (allowedAction === 'premove' && sameColor)
    ) {
      return this;
    }

    const moves = isOutOfOrder
      ? getMovesAfterNullMove(chess, from)
      : allowedAction === 'move'
        ? getMoves(chess, from)
        : getPremoves(chess, from);

    return this.toTargeting(allowedAction, from, moves, isOutOfOrder);
  }
}

export class SelectionTargeting {
  name = 'targeting' as const;

  to: undefined;
  promotion: undefined;
  toTargeting: undefined;

  private selfClickedOrDropped = false;

  constructor(
    public action: Action,
    public from: SquareName,
    public moves: IPotentialMove[],
    public isOutOfOrder: boolean = false,
  ) {}

  toIdle() {
    return new SelectionIdle();
  }

  toSelected(to: SquareName) {
    return new SelectionSelected(this.action, this.from, to);
  }

  toPromoting(to: SquareName, promotion: IPotentialMove) {
    return new SelectionPromoting(this.action, this.from, to, promotion, this.isOutOfOrder);
  }

  transition({
    to,
    canReset,
    canReselect,
    chess,
    moveCallback,
    autoPromotion,
  }: {
    to: SquareName;
    canReset: boolean;
    canReselect: boolean;
    chess: Chess;
    moveCallback: (move: IPotentialMove, isOutOfOrder: boolean) => void;
    autoPromotion?: PromotionPiece;
  }): SelectionTargeting | SelectionIdle | SelectionSelected | SelectionPromoting {
    if (this.from === to) {
      this.selfClickedOrDropped = true;
      return this;
    }

    const fromColor = chess.get(this.from)?.color;
    const pieceColor = chess.get(to)?.color;
    let move = this.moves?.find((m) => m.to === to);
    // TODO: удалить, когда бэк поддержит рокировки королём на ладью
    if (move?.originalMove) {
      move = move.originalMove;
    }

    if (move) {
      if (move.promotion && !autoPromotion) {
        return this.toPromoting(to, move);
      } else {
        const m = structuredClone(move);
        m.promotion = move.promotion && autoPromotion;
        moveCallback(m, this.isOutOfOrder);
        return this.toSelected(to);
      }
    } else if (canReselect && pieceColor && pieceColor === fromColor) {
      return this.toIdle().transition({
        from: to,
        allowedAction: this.action,
        chess,
      });
    } else if (canReset) {
      return this.toIdle();
    }

    return this;
  }

  transitionCleanup() {
    return this.selfClickedOrDropped ? this.toIdle() : this;
  }
}

export class SelectionPromoting {
  name = 'promoting' as const;

  moves: undefined;
  toPromoting: undefined;

  constructor(
    public action: Action,
    public from: SquareName,
    public to: SquareName,
    public promotion: IPotentialMove,
    public isOutOfOrder: boolean = false,
  ) {}

  toIdle() {
    return new SelectionIdle();
  }

  toTargeting(from: SquareName, moves: IPotentialMove[]) {
    return new SelectionTargeting(this.action, from, moves, this.isOutOfOrder);
  }

  toSelected() {
    return new SelectionSelected(this.action, this.from, this.to);
  }
}

export class SelectionSelected {
  name = 'selected' as const;

  moves: undefined;
  promotion: undefined;
  toSelected: undefined;
  toPromoting: undefined;
  toTargeting: undefined;

  constructor(
    public action: Action,
    public from: SquareName,
    public to: SquareName,
  ) {}

  toIdle() {
    return new SelectionIdle();
  }
}

/*
    ____,-------------------------------,____
    \   |            Helpers            |   /
    /___|-------------------------------|___\
*/

function getMoves(chess: Chess, from: SquareName): IPotentialMove[] {
  const moves = chess.moves({ verbose: true, square: from });
  return fix960Castling(from, chess, moves);
}

function getMovesAfterNullMove(chess: Chess, from: SquareName): IPotentialMove[] {
  const future = new Chess(chess.fen(), {
    enable960: true,
    skipValidation: true,
  });
  applyNullMove(future);
  return getMoves(future, from);
}

function getPremoves(chess: Chess, from: SquareName): IPotentialMove[] {
  const moves: Record<string, IPotentialMove> = {};

  chess.moves({ verbose: true }).forEach(({ after: fen }) => {
    const futureChess = new Chess(fen, { enable960: true });

    fix960Castling(from, futureChess, futureChess.moves({ verbose: true, square: from })).forEach(
      (m) => (moves[m.to] = m),
    );
  });

  return Object.values(moves);
}

function fix960Castling(from: SquareName, chess: Chess, moves: IPotentialMove[]): IPotentialMove[] {
  const normalCastlingMoves: IPotentialMove[] = [];

  return moves
    .map((m) => {
      // SIC! Специальные правила для 960: для рокировки короля ставят прямо на
      // ладью (что может быть чревато для корректной обработки премува, если
      // ладью съедят)

      const isCastling = m.san === 'O-O-O' || m.san === 'O-O';
      if (isCastling) {
        const rookSquare = chess.getCastlingSquares(chess.get(from)!.color)[
          m.san === 'O-O' ? 'k' : 'q'
        ] as SquareName;

        const isKingOk = from === 'e1' || from === 'e8';
        const isRookOk =
          rookSquare === 'a1' || rookSquare === 'a8' || rookSquare === 'h1' || rookSquare === 'h8';
        const is960 = !isKingOk || !isRookOk;

        if (is960) {
          // меняем существующий ход для 960
          m.to = rookSquare;
        } else {
          // добавляем альтернативный ход для обычных шахмат
          const n: IPotentialMove = structuredClone(m);
          n.to = rookSquare;
          normalCastlingMoves.push(n);
          // TODO: удалить, когда бэк поддержит рокировки королём на ладью
          n.originalMove = m;
        }
      }

      return m;
    })
    .concat(normalCastlingMoves);
}
